<template>
  <v-container fluid class="px-8">
    <v-overlay :value="add_user">
      <AddUserCard
        v-on:close_add_user="close_add_user"
        v-on:add_members_to_org="add_members_to_org"
        v-on:invite_member_to_org="invite_member_to_org"
        :availableUsers="availableUsers"
      >
      </AddUserCard>
    </v-overlay>
    <v-overlay :value="remove_user">
      <RemoveUserCard
        :user="user_to_remove"
        :org="$auth.user.org_id"
        v-on:close_remove_user="close_remove_user"
        v-on:remove_member_from_org="remove_member_from_org"
        z-index="0"
      />
    </v-overlay>
    <v-overlay :value="edit_user">
      <EditUserCard
        :user="user_to_edit"
        v-on:close_edit_user="close_edit_user"
        v-on:edit_user="edit_org_user"
        z-index="0"
      />
    </v-overlay>
    <div v-if="showAlert">
      <v-alert
        class="v-alert"
        v-model="showAlert"
        border="left"
        close-text="Close Alert"
        dismissible
        :type="alertType"
        style="position: absolute"
      >
        {{ alertMsg }}
      </v-alert>
    </div>
    <v-row class="ma-0 pa-0">
      <v-col cols="12">
        <v-row class="ma-0 pa-0" align="center">
          <v-col cols="auto" class="ma-0 pa-0">
            <span class="page-title">Admin</span>
          </v-col>
        </v-row>
      </v-col>
      <v-col>
        <v-row class="ma-0 pa-0">
          <v-col cols="12" class="ma-0 pa-0 section-title">
            <div style="flex: 2">Org Users</div>
            <div>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click="add_user = true" :disabled="Object.keys(orgUsers).length == 0">
                    <v-icon color="secondary">mdi-account-plus</v-icon>
                  </v-btn>
                </template>
                <TooltipDescription title="Add user to the org"></TooltipDescription>
              </v-tooltip>
            </div>
          </v-col>
          <v-col cols="12" class="ma-0 pa-0 pt-6" v-if="Object.keys(orgUsers).length == 0">
            <v-row class="ma-0 pa-0" style="width: 100%" justify="space-around">
              <v-col cols="auto">
                <v-progress-circular indeterminate color="primary" size="100"></v-progress-circular>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" class="ma-0 pa-0 pt-6" v-else>
            <v-expansion-panels multiple flat>
              <v-expansion-panel
                v-for="group in groupedOrgUsers"
                :key="group.domain"
              >
                <v-expansion-panel-header class="user-domain-header pa-0">
                  <div class="user-domain-title-group">
                    <span class="user-domain-title">{{ group.domain }}</span>
                    <span class="user-domain-count">({{ group.users.length }})</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-list one-line dense>
                    <v-list-item
                      v-for="user in group.users"
                      :key="user.email"
                      class="ma-0 pa-0"
                    >
                      <v-list-item-avatar v-if="user.picture">
                        <img :src="user.picture" :alt="user.name" />
                      </v-list-item-avatar>

                      <v-list-item-content>
                        <v-list-item-title v-text="user.name"></v-list-item-title>
                        <v-list-item-subtitle v-text="user.email"></v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action style="width: 200px;">
                        <v-select
                          class="ma-0 pa-0"
                          :items="['VIEWER','ADMIN','SUPER_ADMIN']"
                          v-model="user.access"
                          label="Access Level"
                          @change="update_permissions(user.userId, user)"
                          hide-details
                        ></v-select>
                      </v-list-item-action>
                      <v-list-item-action class="user-action-buttons">
                        <div class="user-action-button-group">
                          <v-tooltip right>
                            <template v-slot:activator="{ on }">
                              <span v-on="on" class="user-action-tooltip-target">
                                <v-btn
                                  icon
                                  :disabled="!can_edit_user(user.userId)"
                                  @click="open_edit_user(get_editable_user(user))"
                                >
                                  <v-icon color="secondary">mdi-account-edit</v-icon>
                                </v-btn>
                              </span>
                            </template>
                            <TooltipDescription
                              :title="get_edit_user_tooltip(user.userId)"
                            ></TooltipDescription>
                          </v-tooltip>
                          <v-tooltip right>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                icon
                                v-on="on"
                                @click="open_remove_user_confirmation(user.userId)"
                              >
                                <v-icon color="secondary">mdi-account-remove</v-icon>
                              </v-btn>
                            </template>
                            <TooltipDescription
                              title="Remove user from the organization"
                            ></TooltipDescription>
                          </v-tooltip>
                        </div>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  ALERT_GENERIC_MESSAGE,
  ALERT_TYPE_ERROR,
  SUCCESS_USER_INVITATION_MESSAGE,
  SUCCESS_ADD_USER_MESSAGE,
  SUCCESS_REMOVE_USER_MESSAGE,
} from "@/constants/constants.js";
import admin_api_caller from "@/javascript/user_org_retrieval";
import TooltipDescription from "../../UtilityComponents/TooltipDescription.vue";
import RemoveUserCard from "./AdminComponents/RemoveUser.vue";
import AddUserCard from "./AdminComponents/AddUser.vue";
import EditUserCard from "./AdminComponents/EditUser.vue";

export default {
  name: "AdminPage",
  props: [],
  components: {
    TooltipDescription,
    RemoveUserCard,
    AddUserCard,
    EditUserCard,
  },
  data: () => ({
    orgUsers: {},
    alertMsg: null,
    alertType: null,
    showAlert: false,
    remove_user: false,
    add_user: false,
    edit_user: false,
    user_to_remove: null,
    user_to_edit: null,
    all_users: {},
  }),
  mounted() {
    if (this.$auth.isAuthenticated) {
      this.get_my_permissions();
      this.get_org_users();
      this.get_all_users();
    }
  },
  computed: {
    availableUsers: function () {
      let availableUser = {};
      for (let userId in this.all_users) {
        if (!this.orgUsers[userId]) {
          availableUser[userId] = this.all_users[userId];
        }
      }
      return availableUser;
    },
    groupedOrgUsers: function () {
      const groupedUsers = {};

      for (let userId in this.orgUsers) {
        const user = this.get_display_user(userId);
        const email = user.email || "";
        const emailParts = email.split("@");
        const domain =
          emailParts.length > 1
            ? emailParts[emailParts.length - 1].toLowerCase()
            : "unknown";

        if (!groupedUsers[domain]) {
          groupedUsers[domain] = [];
        }

        groupedUsers[domain].push({
          ...user,
          userId,
        });
      }

      return Object.keys(groupedUsers)
        .sort((firstDomain, secondDomain) =>
          firstDomain.localeCompare(secondDomain)
        )
        .map((domain) => ({
          domain,
          users: groupedUsers[domain].sort((firstUser, secondUser) =>
            (firstUser.email || "").localeCompare(secondUser.email || "")
          ),
        }));
    },
  },
  methods: {
    get_all_users: async function () {
      let token = await this.$auth.getTokenSilently();
      let allUsers = await admin_api_caller
        .get_all_users(token)
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch((e) => {
          console.log(e);
          return false;
        });
      if (allUsers) {
        this.all_users = allUsers;
      } else {
        this.triggerAlert(ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
    },
    get_org_users: async function () {
      let token = await this.$auth.getTokenSilently();
      let orgUsers = await admin_api_caller
        .get_org_users(token, this.$auth.user.org_id)
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch((e) => {
          console.log(e);
          return false;
        });
      if (orgUsers) {
        this.orgUsers = orgUsers;
      } else {
        this.triggerAlert(ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
    },
    add_members_to_org: async function (userIds) {
      let token = await this.$auth.getTokenSilently();
      let resp = await admin_api_caller
        .add_org_members(token, this.$auth.user.org_id, userIds)
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch((e) => {
          console.log(e);
          return false;
        });
      if (resp && resp["response_code"] == 204) {
        this.triggerAlert("success", SUCCESS_ADD_USER_MESSAGE);
        this.get_org_users();
      } else {
        this.triggerAlert(ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
      this.add_user = false;
    },
    invite_member_to_org: async function (inviteeEmail) {
      let token = await this.$auth.getTokenSilently();
      let resp = await admin_api_caller
        .invite_org_member(
          token,
          this.$auth.user.org_id,
          this.$auth.user.name,
          inviteeEmail
        )
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch((e) => {
          console.log(e);
          return false;
        });
      if (resp && (resp["response_code"] == 200 || resp["response_code"] == 201)) {
        this.triggerAlert("success", SUCCESS_USER_INVITATION_MESSAGE);
      } else {
        this.triggerAlert(ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
      this.add_user = false;
    },
    remove_member_from_org: async function () {
      let token = await this.$auth.getTokenSilently();
      let resp = await admin_api_caller
        .remove_org_member(token, this.$auth.user.org_id, this.user_to_remove)
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch((e) => {
          console.log(e);
          return false;
        });
      if (resp && resp["response_code"] == 204) {
        this.triggerAlert("success", SUCCESS_REMOVE_USER_MESSAGE);
        this.get_org_users();
      } else {
        this.triggerAlert(ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
      this.remove_user = false;
    },
    open_remove_user_confirmation: function (userId) {
      this.user_to_remove = userId;
      this.remove_user = true;
    },
    close_remove_user: function () {
      this.user_to_remove = "";
      this.remove_user = false;
    },
    open_edit_user: function (user) {
      this.user_to_edit = { ...user };
      this.edit_user = true;
    },
    get_editable_user: function (user) {
      return {
        ...user,
        ...this.all_users[user.userId],
        userId: user.userId,
      };
    },
    get_display_user: function (userId) {
      const orgUser = this.orgUsers[userId] || {};
      const profileUser = this.all_users[userId] || {};
      const givenName = profileUser.given_name || profileUser.first_name || "";
      const lastName = profileUser.last_name || profileUser.family_name || "";
      const profileName = `${givenName} ${lastName}`.trim();

      return {
        ...orgUser,
        ...profileUser,
        userId,
        access: orgUser.access,
        name: profileName || profileUser.name || orgUser.name,
      };
    },
    can_edit_user: function (userId) {
      // Check if the user is a valid Auth0 user and must either be a database connection (using the Auth0 store)
      return (userId || "").startsWith("auth0|");
    },
    get_edit_user_tooltip: function (userId) {
      if (this.can_edit_user(userId)) {
        return "Edit user";
      }
      return "User edit is not supported for the connection type";
    },
    close_edit_user: function () {
      this.user_to_edit = null;
      this.edit_user = false;
    },
    edit_org_user: async function (userPayload) {
      let token = await this.$auth.getTokenSilently();
      let resp = await admin_api_caller
        .edit_user(token, userPayload)
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch((e) => {
          console.log(e);
          return false;
      });
      if (resp && (resp["response_code"] == 200 || resp["response_code"] == 204)) {
        this.triggerAlert("success", "User updated");
        this.apply_user_update(userPayload);
        await this.get_org_users();
        await this.get_all_users();
        this.apply_user_update(userPayload);
      } else {
        this.triggerAlert(ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
      this.close_edit_user();
    },
    apply_user_update: function (userPayload) {
      const userId = userPayload.user_id;
      const givenName = userPayload.given_name || "";
      const lastName = userPayload.family_name || "";
      const name = `${givenName} ${lastName}`.trim();
      const updatedUser = {
        given_name: givenName,
        first_name: givenName,
        last_name: lastName,
        family_name: lastName,
        name,
      };

      if (userPayload.email) {
        updatedUser.email = userPayload.email;
      }

      this.$set(this.all_users, userId, {
        ...this.all_users[userId],
        ...updatedUser,
      });

      if (this.orgUsers[userId]) {
        this.$set(this.orgUsers, userId, {
          ...this.orgUsers[userId],
          ...updatedUser,
        });
      }
    },
    close_add_user: function () {
      this.add_user = false;
    },
    triggerAlert: function (type, message) {
      this.alertType = type ? type : "info";
      this.alertMsg = message;
      this.showAlert = true;
    },
    update_permissions: async function(userID, user){
      console.log(user);
      let token = await this.$auth.getTokenSilently();
      let resp = await admin_api_caller
        .update_org_member_permissions(token, userID, user['access'])
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch((e) => {
          console.log(e);
          return false;
        });
      console.log(resp);

      // if (resp && resp['response_code'] == 204) {
      //   this.triggerAlert("success", "User removed from org");
      //   this.get_org_users();
      // } else {
      //   this.triggerAlert(ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      // }
    },
    get_my_permissions: async function() {
      let token = await this.$auth.getTokenSilently();
      let resp = await admin_api_caller
        .get_my_permissions(token, this.$auth.user.sub)
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch((e) => {
          console.log(e);
          return false;
        });
      if (resp && resp['response_code'] == 204) {
        console.log(resp)
        if(resp['access'] !== 'SUPER_ADMIN'){
          this.$router.replace('/home');
        }
      } 
      this.remove_user = false;
    }
  },
};
</script>

<style>
.page-title {
  font-family: "Gothic Bold" !important;
  font-size: 25pt !important;
}
.section-title {
  font-family: "Gothic Bold" !important;
  font-weight: 400 !important;
  font-size: 1.5em !important;
  margin-top: 10px;

  display: flex;
}

.v-overlay__content {
  width: 100%;
  height: unset !important;
}
.user-domain-header {
  min-height: 56px !important;
}
.user-domain-title-group {
  display: inline-flex;
  align-items: center;
}
.user-domain-title {
  font-family: "Gothic" !important;
  font-weight: 600 !important;
}
.user-domain-count {
  font-family: "Gothic" !important;
  font-size: 0.85em !important;
  margin-left: 4px;
}
.user-action-buttons {
  min-width: 96px;
}
.user-action-button-group {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.user-action-tooltip-target {
  display: inline-flex;
}
</style>
